/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { isExternalLink } from 'bundles/common/utils/urlUtils';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

const styles: Record<string, Interpolation<Theme>> = {
  card: css`
    border-radius: 8px;
    box-shadow: rgb(31 31 31 / 10%) 0 3px 10px;
    padding: 16px;
    border: 2px solid var(--cds-color-neutral-disabled-strong);

    :hover {
      border: 2px solid var(--cds-color-neutral-stroke-primary);
    }
  `,
  link: css`
    &:hover,
    &:focus {
      text-decoration: none;
    }

    display: block;
    height: 100%;
  `,
  paddingY: css`
    padding-top: 16px;
  `,
  text: css`
    min-height: 76px;
  `,
};

type Props = {
  title?: string | null;
  description?: string | null;
  link?: string | null;
};

const GridLinksCard = ({ title, description, link }: Props) => {
  const { trackClickButton } = useDegreeDescriptionEventingContext();
  if (!link) return null;

  const handleOnClick = () => {
    trackClickButton('degree_grid_link', 'page_body', link, title);
  };

  return (
    <div css={styles.card}>
      <TrackedLink2
        href={link}
        trackingName="grid_link"
        target={isExternalLink(link) ? '_blank' : undefined}
        rel={isExternalLink(link) ? 'noopener noreferrer' : undefined}
        css={styles.link}
        onClick={handleOnClick}
      >
        {title && (
          <Typography2 component="h3" variant="subtitleMedium">
            {title}
          </Typography2>
        )}
        {description && (
          <Typography2 component="p" variant="bodySecondary" css={[styles.paddingY, styles.text]} color="supportText">
            {description}
          </Typography2>
        )}
      </TrackedLink2>
    </div>
  );
};

export default GridLinksCard;
